import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useApi, useCurrent, useSession } from '@/lib/session';
import { setupPrefs } from '@/lib/storage';
import { buildSetup, type ApiSetupStep } from './steps';

export const SETUP_KEY = ['setup'] as const;

/**
 * Passos de configuração do negócio atual, já filtrados pelas permissões da pessoa. Só quem configura o
 * negócio (dono e administrador) vê a configuração guiada; suporte em "entrar como" não vê.
 */
export function useSetup(opts: { pollMs?: number | false } = {}) {
  const api = useApi();
  const { me } = useSession();
  const { current, can, impersonating } = useCurrent();
  const userId = me?.user.id ?? '';
  const tenantId = current?.tenantId ?? '';
  const enabled = !!userId && !!tenantId && !impersonating && can('dashboard:read') && can('tenant:update');

  const query = useQuery({
    queryKey: [...SETUP_KEY, tenantId],
    queryFn: () => api.get<{ steps: ApiSetupStep[] }>('/v1/core/dashboard/setup'),
    enabled,
    staleTime: 5_000,
    refetchOnWindowFocus: true,
    refetchInterval: opts.pollMs ?? false,
  });

  // Lido do navegador a cada troca de pessoa/negócio; `version` força reler depois de pular um passo.
  const [version, setVersion] = useState(0);
  const skipped = useMemo(() => (enabled ? setupPrefs.skipped(userId, tenantId) : []), [enabled, userId, tenantId, version]); // eslint-disable-line react-hooks/exhaustive-deps
  const skip = useCallback(
    (key: string) => {
      setupPrefs.setSkipped(userId, tenantId, [...new Set([...setupPrefs.skipped(userId, tenantId), key])]);
      setVersion((v) => v + 1);
    },
    [userId, tenantId],
  );

  const state = useMemo(() => (query.data ? buildSetup(query.data.steps, can, skipped) : null), [query.data, can, skipped]);
  return { enabled, state, userId, tenantId, skip, isFetching: query.isFetching };
}
